import BpmnJS from 'bpmn-js/lib/Viewer';
import Canvas from 'diagram-js/lib/core/Canvas';
import { useCallback, useRef, useState } from 'react';

interface UseDiagramDragProps {
  bpmnModelerRef: React.RefObject<BpmnJS | null>;
  currentScale: number;
}

export const useDiagramDrag = ({ bpmnModelerRef, currentScale }: UseDiagramDragProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 0) {
      setIsDragging(true);
      lastPos.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
    }
  }, []);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (event.touches.length === 1) {
      setIsDragging(true);
      lastPos.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      event.preventDefault();
    }
  }, []);

  const handleMove = useCallback(
    (x: number, y: number) => {
      if (isDragging && bpmnModelerRef.current) {
        const canvas = bpmnModelerRef.current.get<Canvas>('canvas');
        const deltaX = x - lastPos.current.x;
        const deltaY = y - lastPos.current.y;

        const viewbox = canvas.viewbox();
        canvas.viewbox({
          x: viewbox.x - deltaX / currentScale,
          y: viewbox.y - deltaY / currentScale,
          width: viewbox.width,
          height: viewbox.height,
        });

        lastPos.current = { x, y };
      }
    },
    [isDragging, bpmnModelerRef, currentScale]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      handleMove(event.clientX, event.clientY);
    },
    [handleMove]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (event.touches.length === 1) {
        handleMove(event.touches[0].clientX, event.touches[0].clientY);
        event.preventDefault();
      }
    },
    [handleMove]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      handleEnd();
      event.preventDefault();
    },
    [handleEnd]
  );

  return {
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
